// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { screen } from "@bg/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { css } from "styled-components";

const enum fields {
    imageAlignment = "imageAlignment",
}

interface AboutUsRowProps extends WidgetProps {
    fields: {
        [fields.imageAlignment]: "left" | "right";
    };
}

interface AboutUsRowStyles {
    wrapper?: InjectableCss;
    pageContent?: InjectableCss;
    imageWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
}

const styles: AboutUsRowStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: none;
            align-items: center;
            align-items: center;

            @media (min-width: 1200px) {
                display: flex;
            }
        `,
    },
    pageContent: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            position: relative;
        `,
    },
};

const AboutUsRowWidget: React.FC<AboutUsRowProps> = (props: any) => {
    const {
        id,
        fields: { imageAlignment },
    } = props;
    const isLeftAlignment = imageAlignment === "left";

    const [winWidth, setWinWidth] = React.useState(1024);
    const [textWidth, setTextWidth] = React.useState(475);
    const [imgWidth, setImageWidth] = React.useState(475);
    const [containerWidth, setContainerWidth] = React.useState(1024);

    React.useEffect(() => {
        getWindowWidth();
        window.addEventListener("resize", getWindowWidth);
        return () => {
            window.removeEventListener("resize", getWindowWidth);
        };
    }, []);

    const getWindowWidth = () => {
        if (winWidth !== window.innerWidth) {
            setWinWidth(window.innerWidth);
        }
        const container: any = document.getElementsByClassName("about-us-container")[0];
        if (container) {
            // setContainerWidth(container.offsetWidth - 80); // 80 is the Page padding in the component
            setContainerWidth(container.offsetWidth); // 80 is the Page padding in the component
        }
    };

    React.useEffect(() => {
        if (winWidth > 1024 && containerWidth > 1024) {
            const imgWidth = containerWidth - textWidth + (winWidth - containerWidth) / 2;
            setImageWidth(imgWidth);
        }
    }, [winWidth]);

    return (
        <>
            {/* Large Screens view goes here */}
            <StyledWrapper {...styles.wrapper} className="about-us-container">
                <Page
                    as="div"
                    css={css`
                        &&& {
                            padding-left: 0;
                            padding-right: 0;
                        }
                    `}
                >
                    <StyledWrapper {...styles.pageContent}>
                        <StyledWrapper
                            css={css`
                                position: absolute;
                                top: 0;
                                width: ${imgWidth}px;
                                display: flex;
                                ${isLeftAlignment ? `right: ${textWidth}px;` : `left: ${textWidth}px;`}
                                ${isLeftAlignment ? "justify-content: flex-end;" : "justify-content: flex-start;"}
                                img {
                                    max-height: 450px;
                                    max-width: 100%;
                                }
                            `}
                        >
                            <Zone contentId={id} zoneName="Content1" />
                        </StyledWrapper>

                        <StyledWrapper
                            css={css`
                                width: ${textWidth}px;
                                position: absolute;
                                top: 0;
                                height: 100%;
                                display: flex;
                                align-items: center;
                                ${isLeftAlignment ? "padding-left: 40px;" : "padding-right: 40px;"}
                                ${isLeftAlignment ? "right: 0;" : "left: 0;"}
                            `}
                        >
                            <Zone contentId={id} zoneName="Content2" />
                        </StyledWrapper>
                        <StyledWrapper
                            css={css`
                                visibility: hidden;
                                display: flex;
                            `}
                        >
                            <StyledWrapper
                                css={css`
                                    img {
                                        max-height: 450px;
                                        height: 450px;
                                        max-width: 450px;
                                    }
                                `}
                            >
                                <Zone contentId={id} zoneName="Content1" />
                            </StyledWrapper>

                            <StyledWrapper>
                                <Zone contentId={id} zoneName="Content2" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </Page>
            </StyledWrapper>
            {/* Small Screens view goes here */}
            <StyledWrapper
                css={css`
                    display: none;
                    @media (max-width: 1199px) {
                        display: block;

                        .img-wrpr {
                            height: auto;
                        }
                    }
                `}
            >
                <StyledWrapper>
                    <Zone contentId={id} zoneName="Content1" />
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        margin-top: 30px;
                    `}
                >
                    <Zone contentId={id} zoneName="Content2" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    fieldDefinitions: [
        {
            name: fields.imageAlignment,
            defaultValue: "left",
            editorTemplate: "RadioButtonsField",
            fieldType: "General",
            options: [
                {
                    displayName: "Left",
                    value: "left",
                },
                {
                    displayName: "Right",
                    value: "right",
                },
            ],
        },
    ],
    displayName: "BGS About US Row",
};

const widgetModule: WidgetModule = {
    component: AboutUsRowWidget,
    definition,
};

export default widgetModule;
